import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import ConceptView from '../CP-KnowledgeDB/ConceptView/ConceptView';  // Ajustez le chemin selon votre structure
import ConceptQuiz from './ConceptQuiz';

/**
 * Version améliorée du composant ConceptBasedQuiz
 * Utilise ConceptView pour la sélection des concepts avec validation robuste
 */
const ConceptBasedQuiz = ({ competence, userId, onQuizComplete, makeAuthenticatedRequest }) => {
  console.log("--- DÉMARRAGE CONCEPTBASEDQUIZ ---");
  console.log("Compétence:", competence);
  console.log("UserID:", userId);
  
  // État pour le concept sélectionné
  const [selectedConcept, setSelectedConcept] = useState(null);
  // État pour indiquer si on est en train de charger les concepts
  const [loading, setLoading] = useState(false);
  // État pour stocker les erreurs éventuelles
  const [error, setError] = useState(null);
  // État pour déterminer si on doit afficher la vue de sélection ou le quiz
  const [showConceptSelector, setShowConceptSelector] = useState(true);
  
  // Fonction de validation de concept
  const validateConcept = (concept) => {
    if (!concept) {
      return { valid: false, message: "Le concept est manquant ou invalide" };
    }
    
    // Vérifier que le concept a un titre
    if (!concept.title && !concept.concept) {
      return { valid: false, message: "Le concept n'a pas de titre" };
    }
    
    // Vérifier que le concept a des documents associés
    if (!concept.documents || !Array.isArray(concept.documents) || concept.documents.length === 0) {
      // C'est un avertissement, pas une erreur fatale
      console.warn("Concept sans documents associés:", concept.title);
      // On peut enrichir le concept
      concept.documents = [];
    }
    
    // Vérifier que les documents ont un ID
    if (concept.documents.length > 0) {
      const missingIds = concept.documents.filter(doc => !doc.id && !doc.documentId);
      if (missingIds.length > 0) {
        console.warn(`${missingIds.length} document(s) sans identifiant pour le concept:`, concept.title);
      }
    }
    
    // Normaliser le concept pour assurer la cohérence
    const normalizedConcept = {
      ...concept,
      title: concept.title || concept.concept,
      concept: concept.concept || concept.title,
      documents: concept.documents || []
    };
    
    return { valid: true, concept: normalizedConcept };
  };
  
  // Gestionnaire pour la sélection d'un concept
  const handleConceptSelection = (concept) => {
    console.log("Concept sélectionné:", concept);
    
    // Valider le concept
    const validationResult = validateConcept(concept);
    
    if (!validationResult.valid) {
      console.error("Erreur de validation du concept:", validationResult.message);
      setError(validationResult.message);
      return;
    }
    
    // Utiliser le concept normalisé
    const normalizedConcept = validationResult.concept;
    console.log("Concept normalisé:", normalizedConcept);
    
    // Tout est valide, passer au quiz
    setSelectedConcept(normalizedConcept);
    setShowConceptSelector(false);
  };

  // Gestionnaire pour retourner à la sélection de concept
  const handleBackToConceptSelection = () => {
    console.log("Retour à la sélection");
    setSelectedConcept(null);
    setShowConceptSelector(true);
    // Réinitialiser l'erreur si présente
    if (error) {
      setError(null);
    }
  };

  // Gestionnaire d'erreur
  const handleError = (error) => {
    console.error("Erreur quiz:", error);
    const errorMessage = typeof error === 'string' ? error : 
                          error instanceof Error ? error.message : 
                          "Une erreur s'est produite";
    setError(errorMessage);
    setLoading(false);
  };

  // Gestionnaire à la fin d'un quiz
  const handleQuizComplete = () => {
    if (onQuizComplete) {
      onQuizComplete();
    }
  };

  // Validation des données requises
  if (!userId || !competence || !competence.id) {
    const errorMsg = "Utilisateur ou compétence non spécifiés";
    console.error(errorMsg);
    return (
      <div className="bg-white shadow-md rounded-lg p-6 text-center">
        <h3 className="text-lg font-medium text-red-600">
          Erreur: {errorMsg}
        </h3>
      </div>
    );
  }

  // Affichage en cas d'erreur
  if (error) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6 text-center">
        <h3 className="text-lg font-medium text-red-600">
          Erreur: {error}
        </h3>
        <button 
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          onClick={() => {
            setError(null);
            setSelectedConcept(null);
            setShowConceptSelector(true);
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 flex justify-center items-center min-h-[200px]">
        <div className="animate-spin">
          <Loader className="h-8 w-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {showConceptSelector ? (
        <div className="concept-selector-container">
          <ConceptView
            userId={userId}
            competence={competence}
            onClose={null}  // Ne pas afficher le bouton de fermeture
            makeAuthenticatedRequest={makeAuthenticatedRequest}
            onError={handleError}
            onSelectConcept={handleConceptSelection}  // Propriété personnalisée pour gérer la sélection
          />
        </div>
      ) : (
        <ConceptQuiz
          concept={selectedConcept}
          userId={userId}
          competenceId={competence.id}
          onBack={handleBackToConceptSelection}
          onComplete={handleQuizComplete}
          onError={handleError}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
        />
      )}
    </div>
  );
};

export default ConceptBasedQuiz;